import { AppealThread, FormField, StepDetail, StepGlobal } from "../dtos";

/**
 * §Review-appeal: the pure rules for placing an appeal beside the decision it challenges.
 *
 * Both the anchors (a document row, a field, a pool value) and the opener's verdict are decided here rather
 * than at each render site, for the same reason workflow-manager keeps them in `lib/appeal-threads.ts`: the
 * window ("while the decision stands") and the identity rule ("a document IS its upload row") have to be the
 * SAME rule everywhere, or one surface offers an appeal the server refuses.
 */

export type AppealKind = "document" | "field" | "value";

/** The item a thread hangs on. `uploadUid` is the identity for a document — never the placement key. */
export interface AppealAnchor {
  kind: AppealKind;
  fieldKey: string;
  uploadUid?: string;
}

/** One item on this step whose credit decision could still be challenged, with the reviewer's words. */
export interface AppealableItem extends AppealAnchor {
  label: string;
  reason?: string;
}

/** Flat submission key of a field — MUST match step-form's `fieldKeyOf` for concrete fields. */
const flatKeyOf = (sectionName: string, field: FormField): string =>
  field.k ?? `${sectionName}.${field.l}`;

/** First candidate with actual text. Deliberately not `??`: an EMPTY label must fall through too. */
export const firstFilled = (...candidates: (string | null | undefined)[]): string =>
  candidates.find((candidate) => candidate != null && candidate !== "") ?? "";

/** The capture-site placements whose Builder config opted into appeals — the only appealable sites. */
const appealablePlacements = (detail: StepDetail): StepGlobal[] =>
  (detail.globals ?? []).filter(
    (placement) => placement.allowAppeal === true && placement.mode === "edit"
  );

/** A DOCUMENT is appealable through EVERY live rejected/conditional file, not just the newest: several
 * files can share one placement (multi-file, per-participant, the two faces of a pair), each with its own
 * decision and its own decider, and the server resolves an appeal by upload uid. A superseded row is
 * frozen history — its replacement is a new file awaiting its own decision. */
const documentItems = (placement: StepGlobal): AppealableItem[] => {
  const files = placement.document?.uploads ?? [];
  return files
    .filter(
      (file) =>
        !file.superseded && (file.decision === "REJECTED" || file.decision === "CONDITIONAL")
    )
    .map((file) => ({
      kind: "document" as const,
      fieldKey: placement.ref,
      uploadUid: file.uid,
      label: firstFilled(placement.label, placement.ref),
      reason: file.rejectionReason ?? undefined,
    }));
};

/** A pool VALUE is appealable through a standing rejection on its canonical row. */
const valueItems = (placement: StepGlobal): AppealableItem[] =>
  placement.reviewStatus === "REJECTED"
    ? [
        {
          kind: "value" as const,
          fieldKey: placement.ref,
          label: firstFilled(placement.label, placement.ref),
        },
      ]
    : [];

/** A FIELD is appealable through a standing REJECTED review on a flagged concrete field — RESUBMITTED
 * means the agent already corrected it, so there is nothing left to argue. */
const fieldItems = (detail: StepDetail): AppealableItem[] => {
  const fieldByKey = new Map(
    (detail.formSchema ?? []).flatMap((section) =>
      section.fields.map((field) => [flatKeyOf(section.name, field), field] as const)
    )
  );
  const items: AppealableItem[] = [];
  for (const [key, review] of Object.entries(detail.fieldReviews ?? {})) {
    const field = fieldByKey.get(key);
    if (review?.status !== "REJECTED" || field?.allowAppeal !== true) continue;
    items.push({
      kind: "field",
      fieldKey: key,
      label: firstFilled(field.l, key.split(".").pop(), key),
      reason: review.comment ?? undefined,
    });
  }
  return items;
};

/**
 * What sales could still challenge here.
 *
 * Mirrors the server's window exactly, so nothing is ever offered that the write would refuse — see each
 * collector for its half of the rule. A placement must also be the EDIT (capture) site: the reviewer's own
 * view of the same key must never offer to appeal a decision they made themselves.
 */
export const appealableItems = (detail: StepDetail): AppealableItem[] => [
  ...appealablePlacements(detail).flatMap((placement) =>
    placement.document ? documentItems(placement) : valueItems(placement)
  ),
  ...fieldItems(detail),
];

/**
 * Do two references name the SAME item? Documents match on the upload row, everything else on the key.
 *
 * The fallback to the key when a uid is missing on either side keeps an older envelope working rather than
 * silently treating one file as two different items.
 */
export const sameItem = (left: AppealAnchor, right: AppealAnchor): boolean => {
  if (left.kind !== right.kind) return false;
  if (left.kind === "document" && left.uploadUid && right.uploadUid) {
    return left.uploadUid === right.uploadUid;
  }
  return left.fieldKey === right.fieldKey;
};

/** The item a thread challenges, read off its root comment. */
export const threadAnchor = (thread: AppealThread): AppealAnchor | null => {
  const appeal = thread.comments[0]?.appeal;
  if (!appeal) return null;
  return {
    kind: appeal.subject.kind,
    fieldKey: appeal.subject.fieldKey,
    uploadUid: appeal.subject.uploadUid,
  };
};

/** The threads challenging one item, in the server's order (oldest first). */
export const threadsForAnchor = (
  threads: AppealThread[],
  anchor: AppealAnchor
): AppealThread[] =>
  threads.filter((thread) => {
    const target = threadAnchor(thread);
    return target !== null && sameItem(target, anchor);
  });

/**
 * Does a live thread already cover this item's CURRENT decision?
 *
 * Only the current one blocks: once the file is replaced and rejected again, the old thread reads as
 * superseded and a fresh appeal is offered — which is what "appeal while the decision stands" means the
 * second time round.
 */
export const coveredByThread = (threads: AppealThread[], anchor: AppealAnchor): boolean =>
  threadsForAnchor(threads, anchor).some((thread) => thread.isCurrentDecision);
